/**
 * CLI sites store — the "sites" of the CLI vendor (Zabbix host groups).
 *
 * CLI-managed gear (Cisco/Juniper/Aruba-AOS switches + firewalls) has no
 * cloud API; the api-shim sources its state from Zabbix. `/api/cli/sites/`
 * returns the Zabbix host groups — each a diagnosable unit of CLI gear.
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";

export interface CliSite {
  groupId: string;
  name: string;
  hostCount: number;
}

interface State {
  data: CliSite[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  load: () => Promise<void>;
}

export const useCliSites = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<CliSite[]>("/cli/sites/");
      set({ data: raw, status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({ data: [], status: "error", error: message });
    }
  },
}));
